import React, { useMemo, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';

import Message from '../Message/Message';
import * as Style from './style';
import {
  activeUserAtom,
  messagesAtom,
  participantsAtom,
} from '../../constant/global';

import { authorColors } from '../../utils/colors';
import {
  datesAtom,
  globalFilterModeAtom,
  limitsAtom,
} from '../../constant/filters';
import { filterMessagesByDate, getISODateString } from '../../utils/utils';

function MessageViewer() {
  const limits = useAtomValue(limitsAtom);
  const [activeUser, setActiveUser] = useAtom(activeUserAtom);
  const participants = useAtomValue(participantsAtom);
  const messages = useAtomValue(messagesAtom);
  const filterMode = useAtomValue(globalFilterModeAtom);
  const { start: startDate, end: endDate } = useAtomValue(datesAtom);
  const endDatePlusOne = new Date(endDate);
  endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
  
  const colorMap = useMemo(
    () =>
      participants.reduce(
        (obj, participant, i) => ({
          ...obj,
          [participant]: authorColors[i % authorColors.length],
        }),
        {},
      ),
    [participants],
  );

  const renderedMessages =
    filterMode === 'index'
      ? messages.slice(limits.low - 1, limits.high)
      : filterMessagesByDate(messages, startDate, endDatePlusOne);

  const isLimited = renderedMessages.length !== messages.length;

  useEffect(() => {
    setActiveUser(participants[0] || '');
  }, [setActiveUser, participants]);

  return (
    <Style.Container>
      {messages.length > 0 && (
        <Style.P>
          <Style.Info>
            {isLimited && filterMode === 'index' && (
              <span>
                Showing messages {limits.low} to{' '}
                {Math.min(limits.high, messages.length)} (
                {renderedMessages.length} out of {messages.length})
              </span>
            )}
            {isLimited && filterMode === 'date' && (
              <span>
                Showing messages from {getISODateString(startDate)} to{' '}
                {getISODateString(endDate)}
              </span>
            )}
            {!isLimited && <span>Showing all {messages.length} messages</span>}
          </Style.Info>
        </Style.P>
      )}

      <Style.List>
        {renderedMessages.map((message, i, arr) => {
          const prevMessage = arr[i - 1];

          return (
            <Message
              key={message.index}
              message={message}
              color={colorMap[message.author || '']}
              isActiveUser={activeUser === message.author}
              sameAuthorAsPrevious={
                prevMessage && prevMessage.author === message.author
              }
            />
          );
        })}
      </Style.List>
    </Style.Container>
  );
}

export default React.memo(MessageViewer);
